/* eslint-disable camelcase */
import {
  getAttribution,
  setAttribution,
} from '@common/vanilla_ui/tracking/attribution';
import cogoToast from 'cogo-toast';
import { OtpForm, RegisterForm } from '@common/vanilla_ui/forms/handlers';
import {
  PhoneInput,
  FormFlow,
  Form,
  Select,
  MultiSelect,
  TermsConsent,
  FocusOTPInput, // SingularOTPAutofill,
} from '@common/vanilla_ui/general';
import { retryWithRecaptchaV2Params } from '@common/utils/recaptcha';
import { ResendOtpForm } from '@common/vanilla_ui/forms';
import { slugify } from '@common/utils/string';
import gtmTracking from '@common/vanilla_ui/tracking/gtm';
import registrationTracking from './tracking';
import trackOtpFill from '@common/vanilla_ui/tracking/actions/trackOtpFill';

const POSITION_FIELD_ID = 'exit-intent-rcb-form-otp-position';
const MODAL_ID = 'rcb-exit-intent-modal';
const modalElement = document.getElementById(MODAL_ID);

let loggedIn = false;
let phoneVerified = false;
let isDefaultCountry = true;
let initialUserPayload = {};

const track = (eventName, eventType = 'click', value = null) => {
  if (window.storeEsEvent) {
    window.storeEsEvent(`exit-intent-rcb-form-${eventName}`, eventType, value);
  }
};

const PROGRAM_PRODUCT_MATCHING = {
  Academy: 'software_development',
  'Data Science': 'data_science',
  'MS in CSE': 'neovarsity',
  Devops: 'devops',
  'AI/ML': 'ai_ml',
};

const ACCOUNT_TYPE_MAPPING = {
  'Academy (Software Development)': 'academy',
  'Data Science': 'data_science',
  Devops: 'devops',
  'AI & Machine Learning': 'ai_ml',
};

const PROGRAM_MAPPING = {
  'AI & Machine Learning': 'AI/ML',
  'Academy (Software Development)': 'Academy',
};

const setRcbAttribution = (program) => {
  const isThankYouPage = document.getElementById(
    'thank-you-page-outer-wrapper',
  );
  const isBtnAction = modalElement?.dataset?.isBtnAction === 'true';
  if (isBtnAction) {
    const currentAttribution = getAttribution();
    setAttribution(
      currentAttribution.intent,
      {
        program: PROGRAM_PRODUCT_MATCHING[program],
      },
    );
  } else if (isThankYouPage) {
    setAttribution('rcb_exit_intent_ty_page', {
      program: PROGRAM_PRODUCT_MATCHING[program],
    });
  } else {
    setAttribution('rcb_exit_intent_widget', {
      program: PROGRAM_PRODUCT_MATCHING[program],
    });
  }
};

const getPayload = (values) => {
  const {
    email,
    phone_number,
    otp,
    request_callback,
    program,
    position,
    lead_identification,
  } = values;
  const updatedProgram = PROGRAM_MAPPING[program] || program;

  initialUserPayload = {
    email,
    phone_number,
    request_callback,
    program: updatedProgram,
    position,
  };
  const rcb_prams = {
    lead_identification,
  };
  setRcbAttribution(updatedProgram);
  if (phoneVerified) {
    return {
      user: {
        program: updatedProgram,
        rcb_prams,
        job_title: position,
      },
      attributions: getAttribution(),
    };
  }

  return {
    user: {
      ...initialUserPayload,
      otp,
      rcb_prams,
    },
    attributions: getAttribution(),
  };
};

const initializeOtpFormInputs = () => {
  new PhoneInput('exit-intent-rcb-form__phone');
  new Select('exit-intent-rcb-form__program');
  if (document.getElementById(POSITION_FIELD_ID)) {
    new Select(POSITION_FIELD_ID);
  }
};


const setupMainForm = () => {
  const formId = 'exit-intent-rcb-form';
  const otpInput = document.getElementById(`${formId}__otp`);
  const otpFormEl = document.getElementById(`${formId}-otp`);
  const submitFormEl = document.getElementById(`${formId}-submit`);
  const emailFormEl = document.getElementById(`${formId}__email`);
  const resendOtpFormEl = document.querySelector(`.${formId}__otp-hint`);
  const lead_identification_present = document.querySelector(`#${formId}-otp`)
    && document
      .querySelector(`#${formId}-otp`)
      .querySelector('#lead_identification');
  const otpFormHeader = document.getElementById(`${formId}-otp-header`);

  TermsConsent.initialize(formId);
  registrationTracking.initialize();

  trackOtpFill({ otpInputId: `${formId}__otp` });

  const form = new FormFlow(
    'exit-intent-rcb-form',
    'exit-intent-rcb-form',
    'page',
    'register',
  );

  const getSourceValue = () => {
    const modalForm = document.getElementById(formId);
    const isBtnAction = modalForm?.dataset?.isBtnAction === 'true';
    return isBtnAction;
  };

  const formFlowEl = document.querySelector('#exit-intent-rcb-form');
  loggedIn = formFlowEl.getAttribute('data-logged-in') === 'true';
  phoneVerified = formFlowEl.getAttribute('data-phone-verified') === 'true';
  isDefaultCountry = formFlowEl.getAttribute('data-default-country') === 'true';

  form.setFormHandler('register', (element) => {
    const { otpMsgForm, otpVoiceForm } = ResendOtpForm.initialize(
      'exit-intent-rcb-form',
      track,
    );
    const endpoint = phoneVerified
      ? '/request-callback'
      : '/users/v2/account/verify';

    const registerForm = new Form(element, {
      method: 'POST',
      endpoint: loggedIn ? endpoint : '/users/v2/verify',
      createPayload: getPayload,
    });

    registerForm.on('submit', () => {
      const sectionSourceValue = getSourceValue()
        ? 'rcb-exit-modal-register'
        : 'rcb-exit-intent-register';
      window.GTMtracker?.pushEvent({
        event: 'gtm_custom_click',
        data: {
          click_type: `${sectionSourceValue}-submit`,
        },
      });
    });

    registerForm.on('submitted', () => {
      const sectionSourceValue = getSourceValue()
        ? 'rcb-exit-modal-register'
        : 'rcb-exit-intent-register';
      window.GTMtracker?.pushEvent({
        event: 'gtm_custom_click',
        data: {
          click_type: `${sectionSourceValue}-submitted`,
        },
      });
    });

    const otpFormId = 'exit-intent-rcb-form-otp';
    const otpForm = new Form(document.getElementById(otpFormId), {
      method: loggedIn ? 'PUT' : 'POST',
      endpoint: loggedIn ? '/users/v2/account' : '/users/v2/',
      createPayload: ({
        'cf-turnstile-response': turnstileResponse,
        program,
        ...values
      }) => ({
        user: {
          ...values,
          program: PROGRAM_MAPPING[program] || program,
          skip_existing_user_check: true,
        },
        type: 'marketing',
        account_type: ACCOUNT_TYPE_MAPPING[program]
          ? ACCOUNT_TYPE_MAPPING[program]
          : 'data_science',
        'cf-turnstile-response': turnstileResponse,
        ...retryWithRecaptchaV2Params(otpFormId),
      }),
      validations: {
        phone_number: 'mobile',
        orgyear: 'required',
      },
    });
    otpForm.on('submit', () => {
      const sectionSourceValue = getSourceValue()
        ? 'rcb-exit-modal-otp'
        : 'rcb-exit-intent-otp';
      window.GTMtracker?.pushEvent({
        event: 'gtm_custom_click',
        data: {
          click_type: `${sectionSourceValue}-submit`,
        },
      });
    });
    otpForm.on('submitted', () => {
      const sectionSourceValue = getSourceValue()
        ? 'rcb-exit-modal-otp'
        : 'rcb-exit-intent-otp';
      window.GTMtracker?.pushEvent({
        event: 'gtm_custom_click',
        data: {
          click_type: `${sectionSourceValue}-submitted`,
        },
      });
    });
    const otpFormSubmittedCb = () => {
      const otp_form_submit_prefix = getSourceValue()
        ? 'rcb-form-otp-submit'
        : 'rcb-exit-intent-form-otp-submit';
      gtmTracking.sendTrackUser(emailFormEl.value);
      const submitFormId = 'exit-intent-rcb-form-submit';
      registerForm.updateField('program', otpForm.getFieldValue('program'));
      if (isDefaultCountry) {
        registerForm.updateField('position', otpForm.getFieldValue('position'));
      }
      const phoneNumberVal = otpForm.getFieldValue('phone_number');
      if (phoneNumberVal) {
        const [countryCode, ...phoneNumbers] = phoneNumberVal.split('-');
        const phoneNumber = phoneNumbers.join('-');
        const countryCodeEl = document.getElementById(
          `input-${submitFormId}__phone-code`,
        );
        const phoneNumberEl = document.getElementById(
          `${submitFormId}__phone-number`,
        );
        countryCodeEl.value = countryCode;
        phoneNumberEl.value = phoneNumber;
      }

      // Auto-Fill OTP Feature
      // SingularOTPAutofill('exit-intent-rcb-form__otp');
      // Focus OTP Field in m-web
      FocusOTPInput('exit-intent-rcb-form-submit', 'exit-intent-rcb-form__otp');
      gtmTracking.sendFormSubmitStatus(
        `${otp_form_submit_prefix}-`
        + `${slugify(otpForm.getFieldValue('program'))}`,
        true,
      );
      registrationTracking.formStatusTracking('true', 'User detail form');
    };

    const enableOtpForm = () => {
      otpFormEl.classList.add('hidden');
      submitFormEl.classList.remove('hidden');
      resendOtpFormEl.classList.remove('hidden');
      otpFormHeader.classList.remove('hidden');
    };

    OtpForm.initialize({
      registerForm,
      otpVoiceForm,
      otpMsgForm,
      initializeInputs: initializeOtpFormInputs,
      parentFormId: formId,
      formId: 'exit-intent-rcb-form-otp',
      track,
      otpForm,
      submittedCallback: otpFormSubmittedCb,
      enableOtpForm,
    });

    const registerFormSubmitCb = () => {
      registerForm.updateField('program', otpForm.getFieldValue('program'));
      if (isDefaultCountry) {
        registerForm.updateField('position', otpForm.getFieldValue('position'));
      }
      if (isDefaultCountry && lead_identification_present) {
        registerForm.updateField(
          'lead_identification',
          otpForm.getFieldValue('lead_identification'),
        );
      }
      if (!phoneVerified) {
        registerForm.updateField('otp', otpInput.value);
      }
    };

    // Please check it
    const isRCBOpened = document.getElementsByClassName(
      'request-callback-container--hidden',
    );

    if (isRCBOpened.length === 1) {
      document.addEventListener('phoneVerificationFailed', () => {
        registrationTracking.formStatusTracking('false', 'otp form', {
          error_message: 'Form is not submitted please check once',
        });
      });
    }

    const registerFormSubmittedCb = () => {
      const register_form_submit_prefix = getSourceValue()
        ? 'rcb-form-submit'
        : 'rcb-exit-intent-form-submit';
      const requestCallbackSuccessEl = document.getElementById(
        'rcb-exit-intent-modal-success',
      );
      const rcbLeftSection = document.getElementById(
        'rcb-exit-intent-modal-left-section',
      );
      const requestCallbackFormEl = document.getElementById(
        'rcb-exit-intent-modal-form',
      );
      requestCallbackSuccessEl?.classList.remove('hidden');
      requestCallbackFormEl?.classList.add('hidden');
      rcbLeftSection?.classList.add('hidden');
      resendOtpFormEl?.classList.add('hidden');
      gtmTracking.sendFormSubmitStatus(
        `${register_form_submit_prefix}-`
        + `${slugify(registerForm.getFieldValue('program'))}`,
        true,
      );
      registrationTracking.formStatusTracking('true', 'otp form');
    };

    const initializeRegisterFormInputs = () => {
      if (!phoneVerified) {
        new PhoneInput('exit-intent-rcb-form-submit__phone');
      }

      if (phoneVerified) {
        const originalSubmit = registerForm.submit.bind(registerForm);
        registerForm.submit = async () => {
          if (!otpForm.getFieldValue('program')) {
            cogoToast.error('Program is required');
            return;
          }
          await originalSubmit();
        };
      }
    };

    RegisterForm.initialize({
      registerForm,
      formId,
      initializeInputs: initializeRegisterFormInputs,
      submitCallback: registerFormSubmitCb,
      submittedCallback: registerFormSubmittedCb,
    });
  });
  form.initialize();

  if (document.querySelector('#lead_identification')) {
    new MultiSelect.Initialize('#lead_identification', {
      search: true,
      maxSelect: 5,
      translations: { items: 'Questions' },
    });
  }
};

const initialize = () => {
  try {
    setupMainForm();
  } catch (error) {
    if (window.GTMtracker) {
      window.GTMtracker.pushEvent({
        event: 'gtm_custom_click',
        data: {
          click_type: 'rcb_exit_intent_form_error',
          click_text: `${error}`,
        },
      });
    }
  }
};

export default { initialize };
